import { useCallback, useEffect, useState } from "react";
import React from "react";
import { useSearchParams } from "react-router-dom";

import DataSourceForm from "../components/DataSourceForm";
import SheetsConnectLead from "../components/SheetsConnectLead";
import DataSourcesTable from "../components/DataSourcesTable";
import { Modal } from "../components/Modal";
import { DatasourceDto, DatasourceDtoTypeEnum } from "./generated/api-client";
import { useDatasources } from "./useDatasources";
import { peekPendingConnection, clearPendingConnection } from "./pending-connection";

const TYPE_MAP: Record<string, DatasourceDtoTypeEnum> = {
  postgres: DatasourceDtoTypeEnum.POSTGRESQL,
  postgresql: DatasourceDtoTypeEnum.POSTGRESQL,
  mongodb: DatasourceDtoTypeEnum.MONGODB,
  "google-sheets": DatasourceDtoTypeEnum.GOOGLE_SHEETS,
  mysql: DatasourceDtoTypeEnum.MYSQL,
};

function dtoFromPending(): DatasourceDto | undefined {
  const parsed = peekPendingConnection();
  if (!parsed) return undefined;
  const type =
    TYPE_MAP[(parsed.type || "postgres").toLowerCase()] ||
    DatasourceDtoTypeEnum.POSTGRESQL;
  return {
    name: "",
    type,
    connectionString: parsed.connectionString,
  };
}

export const useDataSourceManagement = () => {
  const { fetchDatasources } = useDatasources();
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingDto = dtoFromPending();
  const [showForm, setShowForm] = useState(!!pendingDto);
  const [selectedDataSource, setSelectedDataSource] = useState<
    DatasourceDto | undefined
  >(pendingDto);
  const [error, setError] = useState<string | null>(null);

  const handleError = useCallback((err: string) => {
    setError(err);
  }, []);

  const handleSubmit = useCallback((datasource: DatasourceDto) => {
    clearPendingConnection();
    setShowForm(false);
    setSelectedDataSource(undefined);
    fetchDatasources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = useCallback(() => {
    clearPendingConnection();
    setShowForm(false);
    setSelectedDataSource(undefined);
  }, []);

  const openForm = useCallback((datasource?: DatasourceDto) => {
    setSelectedDataSource(datasource);
    setShowForm(true);
  }, []);

  useEffect(() => {
    if (showForm) return;
    const dto = dtoFromPending();
    if (dto) openForm(dto);
  }, [showForm, openForm]);

  // MOSA-908. Choice screen "More connections" lands here with ?connect=postgres.
  useEffect(() => {
    if (searchParams.get("connect") !== "postgres") return;
    openForm({
      name: "",
      type: DatasourceDtoTypeEnum.POSTGRESQL,
      connectionString: "",
    });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("connect");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, openForm, setSearchParams]);

  // An element, not an inner component. A new component type on each open
  // remounts the trigger, so the drawer cannot return focus to it on close.
  const dataSourcesManager = (
    // MOSA-695 item 12: the page owns the one centred column, so every card
    // here is the same width. Do not re-introduce a per-card max-width.
    <div className="flex w-full flex-col gap-4">
      {/* MOSA-364: the pasted Google Sheet link is the primary path.
          PostgreSQL stays behind one secondary link. MySQL and MongoDB
          stay hidden from the lead copy until they are live (U7). */}
      <SheetsConnectLead onMoreConnections={() => openForm()} />
      <DataSourcesTable
        onError={handleError}
        onConnect={() => openForm()}
      />

      <Modal
        open={showForm}
        title={
          selectedDataSource?.id
            ? "Edit data source"
            : "Connect a Data Source"
        }
        subtitle="Connect a database to get your admin panel."
        onClose={handleCancel}
        variant="drawer"
        maxWidth="max-w-2xl"
      >
        <DataSourceForm
          initialData={selectedDataSource}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </Modal>
    </div>
  );

  return {
    dataSourcesManager,
    openForm,
    error,
  };
};
